import React, { useState, useEffect } from "react";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { dataplexApi, authApi } from "../../api/client";
import { Button, Badge } from "../../components/ui";

// Exported for unit testing — reconcile the config response's data-store IDs.
// The API may return a modern `vertex_ai_search_data_store_ids` array and/or a
// legacy single `vertex_ai_search_data_store_id`; fold the legacy value into
// the array (front of list) when it isn't already present.
export function mergeDataStoreIds(res) {
  const ids = [...(res?.vertex_ai_search_data_store_ids || [])];
  const legacy = res?.vertex_ai_search_data_store_id;
  if (legacy && !ids.includes(legacy)) ids.unshift(legacy);
  return ids;
}

// Exported for unit testing — build the save payload from local config state:
// drop blank/whitespace-only IDs and mirror the first surviving ID into the
// legacy single-value field for backward compatibility.
export function buildConfigPayload(config) {
  const cleanIds = (config?.vertex_ai_search_data_store_ids || []).filter((id) => id && id.trim());
  return {
    ...config,
    vertex_ai_search_data_store_ids: cleanIds,
    vertex_ai_search_data_store_id: cleanIds[0] || "",
  };
}

// Exported for unit testing — turn the Gemini connection test's response (or
// error) into one line for the admin. The same GCP settings on this page also
// power Gemini on Vertex AI, and the test reports which sign-in the chat path
// would use: "vertex_ai" (service account) or "ai_studio" (API key).
export function describeGeminiTest(res, err) {
  if (err) {
    const msg = (err && err.message) || "The test could not run.";
    return { ok: false, text: `Not connected: ${msg}` };
  }
  const d = (res && res.data) || {};
  if (!d.connected) return { ok: false, text: "Not connected: no answer from Google." };
  const via = d.auth === "vertex_ai" ? "Vertex AI (service account)"
    : d.auth === "ai_studio" ? "Google AI Studio (API key)" : "Google";
  const n = Number(d.models_available) || 0;
  return { ok: true, text: `Connected through ${via} · ${n} Gemini model${n === 1 ? "" : "s"} available` };
}

export default function DataplexView() {
  const bp = useBreakpoint();

  // ─── GCP Config state ────────────────────────────────
  const [config, setConfig] = useState({
    gcp_project_id: "",
    gcp_location: "global",
    gcp_service_account_json: "",
    vertex_ai_search_data_store_ids: [],
  });
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [saJsonSet, setSaJsonSet] = useState(false);
  const [geminiTesting, setGeminiTesting] = useState(false);
  const [geminiTest, setGeminiTest] = useState(null);

  // ─── Discovery state ─────────────────────────────────
  const [assets, setAssets] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState(null);

  // ─── Available data stores (from Discovery Engine) ────
  const [availableStores, setAvailableStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);

  // ─── Sync state ───────────────────────────────────────
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncTargetStore, setSyncTargetStore] = useState("");
  const [status, setStatus] = useState(null);

  // ─── Semantic Definitions state ───────────────────────
  const [semanticDefs, setSemanticDefs] = useState(null);
  const [overridesText, setOverridesText] = useState("");
  const [semanticSaving, setSemanticSaving] = useState(false);
  const [semanticSaved, setSemanticSaved] = useState(false);

  // ─── Grounding Metrics state ──────────────────────────
  const [groundingReport, setGroundingReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // ─── Section toggle ──────────────────────────────────
  const [expandedSection, setExpandedSection] = useState("config");

  // ─── Load config on mount ────────────────────────────
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setConfigLoading(true);
    try {
      const res = await dataplexApi.getConfig();
      // Merge legacy single ID into the array if present
      const ids = mergeDataStoreIds(res);
      setConfig({
        gcp_project_id: res.gcp_project_id || "",
        gcp_location: res.gcp_location || "global",
        gcp_service_account_json: "",
        vertex_ai_search_data_store_ids: ids,
      });
      setSaJsonSet(res.gcp_service_account_json_set || false);
      if (res.gcp_project_id) fetchDataStores();
    } catch {
      // No config yet
    }
    setConfigLoading(false);
  };

  const fetchDataStores = async () => {
    setStoresLoading(true);
    try {
      const res = await dataplexApi.listDataStores();
      setAvailableStores(res.data_stores || []);
    } catch {}
    setStoresLoading(false);
  };

  const saveConfig = async () => {
    setConfigSaving(true);
    setConfigSaved(false);
    try {
      const payload = buildConfigPayload(config);
      await dataplexApi.updateConfig(payload);
      setConfigSaved(true);
      if (config.gcp_service_account_json) setSaJsonSet(true);
      fetchDataStores();
      setTimeout(() => setConfigSaved(false), 2000);
    } catch {}
    setConfigSaving(false);
  };

  const testGemini = async () => {
    setGeminiTesting(true);
    setGeminiTest(null);
    try {
      setGeminiTest(describeGeminiTest(await authApi.testVertexConfig(), null));
    } catch (e) {
      setGeminiTest(describeGeminiTest(null, e));
    }
    setGeminiTesting(false);
  };

  const discoverAssets = async () => {
    setDiscovering(true);
    setDiscoverError(null);
    try {
      const res = await dataplexApi.discover();
      setAssets(res.assets || []);
    } catch (err) {
      // Was swallowed: a failed discovery showed nothing at all. The API now
      // says what Google refused (e.g. a missing Dataplex permission).
      setDiscoverError(err.message || "Discovery failed.");
    }
    setDiscovering(false);
  };

  const syncAssets = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await dataplexApi.sync({ data_store_id: syncTargetStore || undefined });
      setSyncResult(res);
    } catch (err) {
      setSyncResult({ error: err.message });
    }
    setSyncing(false);
  };

  const loadStatus = async () => {
    try {
      const res = await dataplexApi.getStatus();
      setStatus(res);
    } catch {}
  };

  const loadSemanticDefs = async () => {
    try {
      const res = await dataplexApi.getSemanticDefinitions();
      setSemanticDefs(res);
      setOverridesText(JSON.stringify(res.overrides || {}, null, 2));
    } catch {}
  };

  const saveSemanticOverrides = async () => {
    setSemanticSaving(true);
    setSemanticSaved(false);
    try {
      const parsed = JSON.parse(overridesText);
      await dataplexApi.updateSemanticDefinitions(parsed);
      setSemanticSaved(true);
      setTimeout(() => setSemanticSaved(false), 2000);
    } catch {}
    setSemanticSaving(false);
  };

  const loadGroundingReport = async () => {
    setReportLoading(true);
    try {
      const res = await dataplexApi.getGroundingReport();
      setGroundingReport(res);
    } catch {}
    setReportLoading(false);
  };

  // ─── Section renderer ────────────────────────────────

  const SectionHeader = ({ id, title, subtitle, icon }) => (
    <button
      onClick={() => {
        setExpandedSection(expandedSection === id ? null : id);
        if (id === "status") { loadStatus(); fetchDataStores(); }
        if (id === "semantic") loadSemanticDefs();
        if (id === "metrics") loadGroundingReport();
      }}
      style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 18px", background: COLORS.surface, border: `1px solid ${COLORS.border}`,
        borderRadius: expandedSection === id
          ? `${RADIUS.inner}px ${RADIUS.inner}px 0 0`
          : RADIUS.inner,
        boxShadow: expandedSection === id ? "none" : MATERIALS.spatialShadow(),
        cursor: "pointer", textAlign: "left",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{title}</div>
          <div style={{ fontSize: 11, color: COLORS.textMuted }}>{subtitle}</div>
        </div>
      </div>
      <span style={{ fontSize: 12, color: COLORS.textMuted, transform: expandedSection === id ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
        &#9660;
      </span>
    </button>
  );

  const sectionBody = {
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderTop: "none",
    borderRadius: `0 0 ${RADIUS.inner}px ${RADIUS.inner}px`,
    boxShadow: MATERIALS.spatialShadow(),
    padding: "16px 18px",
  };

  const inputStyle = {
    width: "100%", padding: "8px 12px", fontSize: 12, borderRadius: 8,
    background: COLORS.surface, border: `1px solid ${COLORS.border}`, color: COLORS.text,
    outline: "none", fontFamily: "inherit",
  };

  const labelStyle = { fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4, display: "block" };

  if (configLoading) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.textMuted, fontSize: 13 }}>
        Loading Dataplex configuration...
      </div>
    );
  }

  return (
    <div style={{ flex: 1, padding: bp.isMobile ? "16px 12px 80px" : "24px 28px", overflowY: "auto" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: bp.isMobile ? 18 : 20, fontWeight: 700, color: COLORS.text, letterSpacing: "-0.02em" }}>
          Dataplex & Grounding
        </div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>
          Connect GCP Dataplex, sync assets to Vertex AI Search, and monitor grounding quality
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

        {/* ═══ 1. GCP Configuration ═══ */}
        <div>
          <SectionHeader id="config" title="GCP Configuration" subtitle="Service account, project, and data store settings" icon="&#9881;" />
          {expandedSection === "config" && (
            <div style={sectionBody}>
              <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                <div>
                  <label style={labelStyle}>GCP Project ID</label>
                  <input
                    style={inputStyle}
                    value={config.gcp_project_id}
                    onChange={e => setConfig(prev => ({ ...prev, gcp_project_id: e.target.value }))}
                    placeholder="my-gcp-project"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Location</label>
                  <input
                    style={inputStyle}
                    value={config.gcp_location}
                    onChange={e => setConfig(prev => ({ ...prev, gcp_location: e.target.value }))}
                    placeholder="global"
                  />
                </div>
                <div style={{ gridColumn: bp.isMobile ? "1" : "1 / -1" }}>
                  <label style={labelStyle}>
                    Service Account JSON
                    {saJsonSet && <Badge style={{ marginLeft: 8, fontSize: 9 }} color={COLORS.green}>Configured</Badge>}
                  </label>
                  <textarea
                    style={{ ...inputStyle, height: 100, fontFamily: "monospace", fontSize: 11, resize: "vertical" }}
                    value={config.gcp_service_account_json}
                    onChange={e => setConfig(prev => ({ ...prev, gcp_service_account_json: e.target.value }))}
                    placeholder={saJsonSet ? "Leave empty to keep current credentials. Paste new JSON to replace." : '{"type": "service_account", ...}'}
                  />
                </div>
                <div style={{ gridColumn: bp.isMobile ? "1" : "1 / -1" }}>
                  <label style={labelStyle}>Grounding Data Stores</label>
                  <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 8, lineHeight: 1.5 }}>
                    Select which Vertex AI Search data stores Gemini should query for grounding. These can be stores you sync from Dataplex or pre-existing stores in your GCP project.
                  </div>

                  {/* Fetch button */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <button
                      onClick={fetchDataStores}
                      disabled={storesLoading || !config.gcp_project_id}
                      style={{
                        fontSize: 11, color: COLORS.accentText, background: "none",
                        border: `1px solid ${COLORS.border}`, borderRadius: 8,
                        padding: "5px 12px", cursor: config.gcp_project_id ? "pointer" : "not-allowed",
                        opacity: storesLoading ? 0.6 : 1,
                      }}
                    >
                      {storesLoading ? "Loading..." : "\u{1F504} Fetch Data Stores"}
                    </button>
                    {availableStores.length > 0 && (
                      <>
                        <span style={{ fontSize: 10, color: COLORS.textMuted }}>
                          {availableStores.length} store{availableStores.length !== 1 ? "s" : ""} found
                        </span>
                        <button
                          onClick={fetchDataStores}
                          disabled={storesLoading}
                          style={{
                            fontSize: 10, color: COLORS.textMuted, background: "none",
                            border: "none", cursor: "pointer", padding: "2px 4px",
                            opacity: storesLoading ? 0.5 : 1,
                          }}
                          title="Refresh data store list"
                        >
                          &#x21bb;
                        </button>
                      </>
                    )}
                  </div>

                  {/* Checkbox list of discovered stores */}
                  {availableStores.length > 0 && (
                    <div style={{
                      maxHeight: 180, overflowY: "auto", marginBottom: 8,
                      border: `1px solid ${COLORS.border}`, borderRadius: 8,
                      background: COLORS.surface,
                    }}>
                      {availableStores.map(store => {
                        const checked = (config.vertex_ai_search_data_store_ids || []).includes(store.id);
                        return (
                          <label
                            key={store.id}
                            style={{
                              display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                              cursor: "pointer", borderBottom: `1px solid ${COLORS.border}`,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                setConfig(prev => {
                                  const ids = prev.vertex_ai_search_data_store_ids || [];
                                  const next = checked
                                    ? ids.filter(id => id !== store.id)
                                    : [...ids, store.id];
                                  return { ...prev, vertex_ai_search_data_store_ids: next };
                                });
                              }}
                              style={{ accentColor: COLORS.accent }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, color: COLORS.text, fontWeight: 500 }}>
                                {store.display_name || store.id}
                              </div>
                              <div style={{ fontSize: 10, color: COLORS.textMuted, fontFamily: "monospace" }}>
                                {store.id}
                                {store.create_time && (
                                  <span style={{ marginLeft: 8, color: COLORS.textDim }}>
                                    created {new Date(store.create_time).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {/* Manual fallback input */}
                  <div style={{ display: "flex", gap: 6 }}>
                    <input
                      style={{ ...inputStyle, flex: 1 }}
                      placeholder="Add data store ID manually..."
                      onKeyDown={e => {
                        if (e.key === "Enter" && e.target.value.trim()) {
                          const newId = e.target.value.trim();
                          setConfig(prev => ({
                            ...prev,
                            vertex_ai_search_data_store_ids: [
                              ...(prev.vertex_ai_search_data_store_ids || []).filter(id => id !== newId),
                              newId,
                            ],
                          }));
                          e.target.value = "";
                        }
                      }}
                    />
                    <span style={{ fontSize: 10, color: COLORS.textDim, alignSelf: "center", whiteSpace: "nowrap" }}>
                      Press Enter to add
                    </span>
                  </div>

                  {/* Show manually added IDs not in discovered list */}
                  {(config.vertex_ai_search_data_store_ids || []).filter(id => id && !availableStores.some(s => s.id === id)).length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 4 }}>Manually added:</div>
                      {(config.vertex_ai_search_data_store_ids || [])
                        .filter(id => id && !availableStores.some(s => s.id === id))
                        .map(id => (
                          <div key={id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                            <span style={{ fontSize: 11, color: COLORS.text, fontFamily: "monospace" }}>{id}</span>
                            <button
                              onClick={() => setConfig(prev => ({
                                ...prev,
                                vertex_ai_search_data_store_ids: prev.vertex_ai_search_data_store_ids.filter(i => i !== id),
                              }))}
                              style={{
                                background: "none", border: "none", color: COLORS.pink,
                                cursor: "pointer", fontSize: 12, padding: "0 4px",
                              }}
                            >
                              &times;
                            </button>
                          </div>
                        ))
                      }
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
                {configSaved && <span style={{ fontSize: 11, color: COLORS.green, alignSelf: "center" }}>Saved</span>}
                <Button variant="secondary" onClick={testGemini} disabled={geminiTesting || configSaving}
                  title="Tests the saved settings: Gemini through Vertex AI with this project and service account">
                  {geminiTesting ? "Testing..." : "Test Gemini connection"}
                </Button>
                <Button onClick={saveConfig} disabled={configSaving}>
                  {configSaving ? "Saving..." : "Save Configuration"}
                </Button>
              </div>
              {geminiTest && (
                <div role="status" data-testid="gemini-test-result" style={{
                  marginTop: 8, fontSize: 12, textAlign: "right",
                  color: geminiTest.ok ? COLORS.green : (COLORS.red || "#ef4444"),
                }}>
                  {geminiTest.text}
                </div>
              )}
              <div style={{ marginTop: 6, fontSize: 11, color: COLORS.textMuted, textAlign: "right" }}>
                These settings also power Gemini on Vertex AI. Save first, then test.
              </div>
            </div>
          )}
        </div>

        {/* ═══ 2. Asset Discovery ═══ */}
        <div>
          <SectionHeader id="discover" title="Asset Discovery" subtitle="Discover BigQuery tables and GCS assets from Dataplex Catalog" icon="&#128269;" />
          {expandedSection === "discover" && (
            <div style={sectionBody}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <Button onClick={discoverAssets} disabled={discovering || !config.gcp_project_id}>
                  {discovering ? "Discovering..." : "Discover Assets"}
                </Button>
                {!config.gcp_project_id && (
                  <span style={{ fontSize: 11, color: COLORS.orange }}>Configure GCP Project ID first</span>
                )}
                {assets.length > 0 && (
                  <span style={{ fontSize: 11, color: COLORS.textMuted }}>{assets.length} assets found</span>
                )}
              </div>
              {discoverError && (
                <div role="alert" data-testid="discover-error" style={{
                  marginBottom: 12, padding: "8px 12px", borderRadius: 8, fontSize: 12,
                  background: `${COLORS.pink}10`, border: `1px solid ${COLORS.pink}30`, color: COLORS.pink,
                }}>
                  Discovery failed: {discoverError}
                </div>
              )}

              {assets.length > 0 && (
                <div style={{ maxHeight: 300, overflowY: "auto", border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: COLORS.surface }}>
                        <th style={{ padding: "8px 12px", textAlign: "left", color: COLORS.textMuted, fontWeight: 600, fontSize: 11 }}>Name</th>
                        <th style={{ padding: "8px 12px", textAlign: "left", color: COLORS.textMuted, fontWeight: 600, fontSize: 11 }}>Type</th>
                        <th style={{ padding: "8px 12px", textAlign: "left", color: COLORS.textMuted, fontWeight: 600, fontSize: 11 }}>FQN</th>
                        <th style={{ padding: "8px 12px", textAlign: "right", color: COLORS.textMuted, fontWeight: 600, fontSize: 11 }}>Columns</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assets.map((asset, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                          <td style={{ padding: "8px 12px", color: COLORS.text }}>{asset.display_name || asset.name || "—"}</td>
                          <td style={{ padding: "8px 12px", color: COLORS.textMuted }}>{asset.entry_type || "TABLE"}</td>
                          <td style={{ padding: "8px 12px", color: COLORS.textDim, fontSize: 11, fontFamily: "monospace" }}>
                            {asset.fully_qualified_name || "—"}
                          </td>
                          <td style={{ padding: "8px 12px", color: COLORS.textMuted, textAlign: "right" }}>
                            {asset.columns?.length || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══ 3. Sync to Vertex AI Search ═══ */}
        <div>
          <SectionHeader id="status" title="Sync to Vertex AI Search" subtitle="Import discovered Dataplex assets into a target data store" icon="&#9889;" />
          {expandedSection === "status" && (
            <div style={sectionBody}>
              <div style={{ fontSize: 10, color: COLORS.textDim, marginBottom: 12, lineHeight: 1.5 }}>
                Discovers assets from Dataplex Catalog (BigQuery tables, GCS files) and imports them as documents into the selected Vertex AI Search data store so Gemini can search them for grounding.
              </div>

              {/* Target data store picker */}
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Target Data Store</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select
                    value={syncTargetStore}
                    onChange={e => setSyncTargetStore(e.target.value)}
                    style={{
                      ...inputStyle, flex: 1, cursor: "pointer",
                      appearance: "auto",
                    }}
                  >
                    <option value="">Select a data store...</option>
                    {availableStores.map(store => (
                      <option key={store.id} value={store.id}>
                        {store.display_name || store.id} ({store.id})
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={fetchDataStores}
                    disabled={storesLoading || !config.gcp_project_id}
                    style={{
                      fontSize: 12, color: COLORS.textMuted, background: "none",
                      border: `1px solid ${COLORS.border}`, borderRadius: 8,
                      padding: "7px 10px", cursor: "pointer",
                      opacity: storesLoading ? 0.5 : 1,
                    }}
                    title="Refresh data store list"
                  >
                    &#x21bb;
                  </button>
                </div>
                {!config.gcp_project_id && (
                  <div style={{ fontSize: 10, color: COLORS.orange, marginTop: 4 }}>Configure GCP Project ID and Service Account first</div>
                )}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <Button
                  onClick={syncAssets}
                  disabled={syncing || !config.gcp_project_id || !syncTargetStore}
                >
                  {syncing ? "Syncing..." : "Sync Now"}
                </Button>
                {!syncTargetStore && config.gcp_project_id && (
                  <span style={{ fontSize: 11, color: COLORS.orange }}>Select a target data store above</span>
                )}
              </div>

              {syncResult && (
                <div style={{
                  padding: "12px 14px", borderRadius: 8, fontSize: 12,
                  background: syncResult.error ? `${COLORS.pink}10` : `${COLORS.green}10`,
                  border: `1px solid ${syncResult.error ? COLORS.pink + "30" : COLORS.green + "30"}`,
                  color: syncResult.error ? COLORS.pink : COLORS.green,
                  marginBottom: 14,
                }}>
                  {syncResult.error
                    ? `Sync failed: ${syncResult.error}`
                    : `Sync complete — ${syncResult.imported_count ?? 0} assets imported, ${syncResult.errors?.length ?? 0} errors`
                  }
                </div>
              )}

              {status && (
                <div style={{ fontSize: 12, color: COLORS.textMuted, lineHeight: 1.8 }}>
                  <div><strong style={{ color: COLORS.text }}>Project:</strong> {status.gcp_project_id || "—"}</div>
                  <div><strong style={{ color: COLORS.text }}>Location:</strong> {status.gcp_location || "—"}</div>
                  <div>
                    <strong style={{ color: COLORS.text }}>Semantic Overrides:</strong>{" "}
                    {status.has_semantic_overrides ? "Yes" : "None"}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══ 4. Semantic Definitions ═══ */}
        <div>
          <SectionHeader id="semantic" title="Semantic Definitions" subtitle="Business glossary terms and manual schema overrides" icon="&#128214;" />
          {expandedSection === "semantic" && (
            <div style={sectionBody}>
              {semanticDefs && semanticDefs.glossary_terms?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 8 }}>
                    Auto-discovered Glossary Terms ({semanticDefs.glossary_terms.length})
                  </div>
                  <div style={{
                    maxHeight: 160, overflowY: "auto", padding: "10px 12px",
                    background: COLORS.surface, borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`,
                  }}>
                    {semanticDefs.glossary_terms.map((term, i) => (
                      <div key={i} style={{ fontSize: 12, color: COLORS.text, marginBottom: 4 }}>
                        <strong>{term.display_name || term.name}</strong>
                        {term.description && <span style={{ color: COLORS.textMuted }}> — {term.description}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {semanticDefs && semanticDefs.formatted_context && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 8 }}>
                    Formatted Context (injected into system_instruction)
                  </div>
                  <pre style={{
                    maxHeight: 200, overflowY: "auto", padding: "10px 12px",
                    background: COLORS.surface, borderRadius: RADIUS.deep, border: `1px solid ${COLORS.border}`,
                    fontSize: 11, color: COLORS.textDim, fontFamily: "monospace", whiteSpace: "pre-wrap",
                    margin: 0,
                  }}>
                    {semanticDefs.formatted_context || "No semantic context built yet."}
                  </pre>
                </div>
              )}

              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, marginBottom: 8 }}>
                  Manual Overrides (JSON)
                </div>
                <textarea
                  style={{
                    ...inputStyle, height: 140, fontFamily: "monospace", fontSize: 11,
                    resize: "vertical", whiteSpace: "pre",
                  }}
                  value={overridesText}
                  onChange={e => setOverridesText(e.target.value)}
                  placeholder={'{\n  "_global_terms": {\n    "CLV": "Customer Lifetime Value"\n  }\n}'}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                  {semanticSaved && <span style={{ fontSize: 11, color: COLORS.green, alignSelf: "center" }}>Saved</span>}
                  <Button onClick={saveSemanticOverrides} disabled={semanticSaving}>
                    {semanticSaving ? "Saving..." : "Save Overrides"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ═══ 5. Grounding Metrics ═══ */}
        <div>
          <SectionHeader id="metrics" title="Grounding Metrics" subtitle="Accuracy KPI — target > 95% grounding success rate" icon="&#128200;" />
          {expandedSection === "metrics" && (
            <div style={sectionBody}>
              {reportLoading && (
                <div style={{ fontSize: 12, color: COLORS.textMuted, padding: 20, textAlign: "center" }}>
                  Loading grounding report...
                </div>
              )}

              {!reportLoading && groundingReport && (
                <div>
                  {groundingReport.note && (
                    <div style={{
                      fontSize: 11, color: COLORS.orange, padding: "8px 12px",
                      background: `${COLORS.orange}10`, borderRadius: 8, marginBottom: 14,
                      border: `1px solid ${COLORS.orange}25`,
                    }}>
                      {groundingReport.note}
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
                    {/* Total Grounded Requests */}
                    <div style={{
                      padding: "16px", borderRadius: 10,
                      background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                    }}>
                      <div style={{ fontSize: 10, color: COLORS.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                        Total Grounded
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.text }}>
                        {groundingReport.total_grounded_requests ?? 0}
                      </div>
                    </div>

                    {/* With Chunks */}
                    <div style={{
                      padding: "16px", borderRadius: 10,
                      background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                    }}>
                      <div style={{ fontSize: 10, color: COLORS.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                        With Chunks
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.green }}>
                        {groundingReport.with_grounding_chunks ?? 0}
                      </div>
                    </div>

                    {/* Grounding Rate */}
                    <div style={{
                      padding: "16px", borderRadius: 10,
                      background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                    }}>
                      <div style={{ fontSize: 10, color: COLORS.textDim, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                        Success Rate
                      </div>
                      <div style={{
                        fontSize: 24, fontWeight: 700,
                        color: (groundingReport.grounding_rate ?? 0) >= 0.95 ? COLORS.green : COLORS.orange,
                      }}>
                        {((groundingReport.grounding_rate ?? 0) * 100).toFixed(1)}%
                      </div>
                    </div>
                  </div>

                  {/* Progress bar toward 95% target */}
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: COLORS.textMuted }}>Accuracy Target</span>
                      <span style={{ fontSize: 11, color: COLORS.textMuted }}>
                        {((groundingReport.grounding_rate ?? 0) * 100).toFixed(1)}% / 95%
                      </span>
                    </div>
                    <div style={{
                      height: 8, borderRadius: 4,
                      background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                      overflow: "hidden",
                    }}>
                      <div style={{
                        height: "100%", borderRadius: 4, transition: "width 0.5s ease",
                        width: `${Math.min(((groundingReport.grounding_rate ?? 0) / 0.95) * 100, 100)}%`,
                        background: (groundingReport.grounding_rate ?? 0) >= 0.95
                          ? `linear-gradient(90deg, ${COLORS.green}, #34D399)`
                          : `linear-gradient(90deg, ${COLORS.orange}, #FBBF24)`,
                      }} />
                    </div>
                  </div>

                  <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={loadGroundingReport} disabled={reportLoading} style={{ fontSize: 11, padding: "6px 14px" }}>
                      Refresh
                    </Button>
                  </div>
                </div>
              )}

              {!reportLoading && !groundingReport && (
                <div style={{ fontSize: 12, color: COLORS.textDim, padding: 20, textAlign: "center" }}>
                  No grounding data available yet. Send messages through a Dataplex-grounded agent to start tracking.
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
